import React, { FC } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { GkvBillExportResultDto } from "../../../../../../api-client";

interface ExportResultsDialogProps {
  open: boolean;
  results: GkvBillExportResultDto[];
  onClose: () => void;
}

/**
 * Summary dialog shown after export completes.
 * Shows which bills succeeded and which failed with backend error messages.
 */
const ExportResultsDialog: FC<ExportResultsDialogProps> = ({ open, results, onClose }) => {
  const succeeded = results.filter((r) => r.success);
  const failed = results.filter((r) => !r.success);
  const allSucceeded = failed.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "1.4rem",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 2.5,
          px: 3,
          color: allSucceeded ? "success.main" : "#cc8d34",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {allSucceeded ? (
          <CheckCircleIcon sx={{ fontSize: 28 }} color="success" />
        ) : (
          <ErrorIcon sx={{ fontSize: 28, color: "#cc8d34" }} />
        )}
        Export Zusammenfassung
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Success section */}
        {succeeded.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, fontSize: "1.05rem", color: "success.main", mb: 1.5 }}
            >
              Erfolgreich exportiert ({succeeded.length})
            </Typography>
            {succeeded.map((r) => (
              <Box
                key={r.billId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1.25,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: "rgba(76, 175, 80, 0.06)",
                  border: "1px solid rgba(76, 175, 80, 0.15)",
                  mb: 1,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 22, color: "success.main" }} />
                <Typography color="success.main" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                  {r.billNumber}
                </Typography>
                <Typography sx={{ ml: "auto", fontSize: "1rem" }}>{r.message}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Divider between sections */}
        {succeeded.length > 0 && failed.length > 0 && <Divider sx={{ my: 2.5 }} />}

        {/* Failed section */}
        {failed.length > 0 && (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, fontSize: "1.05rem", color: "error.main", mb: 1.5 }}
            >
              Fehlgeschlagen ({failed.length})
            </Typography>
            {failed.map((r) => (
              <Box
                key={r.billId}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  py: 1.25,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: "rgba(244, 67, 54, 0.04)",
                  border: "1px solid rgba(244, 67, 54, 0.15)",
                  mb: 1,
                }}
              >
                <ErrorIcon sx={{ fontSize: 22, color: "error.main", mt: 0.3 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography color="error.main" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                    {r.billNumber}
                  </Typography>
                  <Typography sx={{ fontSize: "1rem", mt: 0.5, lineHeight: 1.5 }}>
                    {r.message}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          autoFocus
          sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 600, fontSize: "0.95rem" }}
        >
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportResultsDialog;
